/**
 * When the phone bar steps back to its compact size, and when it comes back.
 *
 * The web copy of the app's rule (`apps/mobile/src/shared/state/nav-shrink.ts`,
 * `decideShrink`), so the two bars behave alike (owner, 2026-10-18: "soft
 * shrink with smooth interaction").
 *
 * The first web version answered every frame that moved 8px: a fling's
 * momentum tail and the iPhone's bounce at the bottom of the page wobble
 * either side of zero, so the bar flipped back and forth and never settled.
 * Here the movement adds up, and only a sustained run counts. Reluctant to
 * shrink, quick to come back: a late return means a guest reaching for a tab
 * that has just got smaller.
 */

/** A frame that moved less than this is a finger resting, not a scroll. */
export const SHRINK_THRESHOLD = 6;
/** Never compact near the top; the bar is part of a page's first impression. */
export const SHRINK_TOP_ZONE = 48;
/** Travel needed in one direction before the bar answers. */
export const SHRINK_TRAVEL_DOWN = 40;
export const SHRINK_TRAVEL_UP = 16;

/**
 * Given where the page is, how far it just moved, how far it has moved in this
 * direction already and whether the bar is compact, returns the next carried
 * travel and a target: true compact, false full, null leave it.
 *
 * `maxY` is the furthest the page can scroll. At or past it is the bounce at
 * the bottom, whose settling reads as a scroll up, so it says nothing.
 */
export function decideShrink(
  y: number,
  dy: number,
  compact: boolean,
  carried: number,
  maxY: number = Number.POSITIVE_INFINITY,
): { travel: number; target: boolean | null } {
  // Back at the top the bar always returns, however it got there.
  if (y <= SHRINK_TOP_ZONE) return { travel: 0, target: compact ? false : null };
  // The bottom bounce, either way: leave the bar, and keep the run as it was.
  if (y >= maxY) return { travel: carried, target: null };
  // A frame that barely moved says nothing; it must not clear the run either.
  if (Math.abs(dy) < SHRINK_THRESHOLD) return { travel: carried, target: null };
  // Turning round starts a new run rather than eating into the old one.
  const next = carried * dy < 0 ? dy : carried + dy;
  if (next >= SHRINK_TRAVEL_DOWN && !compact) return { travel: 0, target: true };
  if (next <= -SHRINK_TRAVEL_UP && compact) return { travel: 0, target: false };
  return { travel: next, target: null };
}
